import {
  FindImagesFilterAction,
  FindImagesFilterType,
  FindImagesResultsType,
  MediaItem,
  MediaItemUpload,
  MediaLibraryFilterAction,
  MediaLibraryFilterType
} from '../../../models/media';
import { Reducer } from 'react';
import { FindImagesSource, MediaUploadStatus, PhotoExtensions, VideoExtensions } from '../../../constants/media';
import {
  GetMediaLibraryDocument,
  GetMediaLibraryQuery,
  GetMediaLibraryQueryVariables,
  MediaOrigin,
  MediaType,
  MediaTypeFilter
} from '../../../models/graphql';
import { ApolloClient } from '@apollo/client';
import { getUploadIdFromFileOrUrl } from '../../../helpers/media';

const addItemToMediaLibraryCache = (
  id: string,
  status: MediaUploadStatus,
  mediaItem: MediaItemUpload['completedMediaItem'],
  workspaceId: string | null,
  apolloClient: ApolloClient<object>
) => {
  // Always make sure we have a selected workspace
  if (!workspaceId) return;

  const mediaLibraryData = apolloClient.cache.readQuery<GetMediaLibraryQuery, GetMediaLibraryQueryVariables>({
    query: GetMediaLibraryDocument,
    variables: { workspaceId: workspaceId }
  });

  // If no data is returned, then exit out
  if (!mediaLibraryData) return;

  if (!mediaItem) {
    return;
  }

  // Duplicate check.
  if (mediaLibraryData.workspace.mediaLibrary.items.find(item => item.id === mediaItem.id)) {
    return;
  }

  const obj: GetMediaLibraryQuery['workspace']['mediaLibrary']['items'][number] = {
    __typename: mediaItem.__typename,
    id: mediaItem.id,
    filesize: mediaItem.filesize,
    dateUploaded: mediaItem.dateUploaded,
    type: mediaItem.type,
    archived: mediaItem.archived,
    origin: mediaItem.origin,
    proportions: mediaItem.proportions,
    width: mediaItem.width,
    height: mediaItem.height,
    url: mediaItem.url,
    thumbnailUrl: mediaItem.thumbnailUrl,
    source: mediaItem.source,
    transcoded: mediaItem.__typename === 'Video' ? mediaItem.transcoded : false,
    length: mediaItem.__typename === 'Video' ? (mediaItem.length ? mediaItem.length : 0) : 0,
    filename: mediaItem.__typename === 'Photo' ? mediaItem.filename : '',
    gif: mediaItem.__typename === 'Photo' ? mediaItem.gif : false
  };

  apolloClient.cache.writeQuery<GetMediaLibraryQuery, GetMediaLibraryQueryVariables>({
    query: GetMediaLibraryDocument,
    variables: {
      workspaceId: workspaceId
    },
    data: {
      workspace: {
        ...mediaLibraryData.workspace,
        mediaLibrary: {
          __typename: mediaLibraryData.workspace.mediaLibrary.__typename,
          cursor: mediaLibraryData.workspace.mediaLibrary.cursor,
          count: mediaLibraryData.workspace.mediaLibrary.count + 1,
          items: [...mediaLibraryData.workspace.mediaLibrary.items, obj]
        }
      }
    }
  });
};

const mediaLibraryFilterReducer: Reducer<MediaLibraryFilterType, MediaLibraryFilterAction> = (state, action) => {
  switch (action.type) {
    case 'type': {
      return { ...state, type: action.payload };
    }
    case 'origin': {
      return { ...state, origin: action.payload };
    }
    case 'proportion': {
      return { ...state, proportion: action.payload };
    }
    case 'resetAll':
      return {
        type: '',
        origin: '',
        proportion: '',
        tempType: '',
        tempOrigin: '',
        tempProportion: '',
        resetAll: ''
      };
    default:
      return state;
  }
};

const findImagesFilterReducer: Reducer<FindImagesFilterType, FindImagesFilterAction> = (state, action) => {
  switch (action.type) {
    case 'type': {
      return { ...state, type: action.payload };
    }
    case 'source': {
      return { ...state, source: action.payload };
    }
    case 'proportion': {
      return { ...state, proportion: action.payload };
    }
    case 'resetAll':
      return {
        type: '',
        source: FindImagesSource.PIXABAY,
        proportion: '',
        tempType: '',
        tempSource: '',
        tempProportion: '',
        resetAll: ''
      };
    default:
      return state;
  }
};

const isMediaInFilterScope = (
  media: MediaItem,
  status: MediaUploadStatus,
  appliedFilters: MediaLibraryFilterType,
  allowedStatus?: MediaUploadStatus[]
): boolean => {
  if (allowedStatus && !allowedStatus.includes(status)) {
    return false;
  } else if (appliedFilters.proportion && appliedFilters.proportion !== media.proportion) {
    return false;
  } else if (appliedFilters.origin && appliedFilters.origin !== media.origin) {
    return false;
  } else if (appliedFilters.type && appliedFilters.type !== mapTypeToFilterType(media)) {
    return false;
  }

  return true;
};

const mapCachedMediaToMediaItems = (
  uploads: Record<string, MediaItemUpload>,
  cachedMediaItems: GetMediaLibraryQuery['workspace']['mediaLibrary']['items'] | undefined
): MediaItem[] => {
  const uploadsKeyedByCachedMediaId = Object.values(uploads).reduce((obj, uploadItem) => {
    if (uploadItem.completedMediaItem?.id) {
      obj[uploadItem.completedMediaItem.id] = uploadItem;
    }
    return obj;
  }, {} as Record<string, MediaItemUpload>);

  const mappedItemsArray: MediaItem[] = [];

  cachedMediaItems?.forEach(item => {
    // If the item was uploaded in this session, don't map it, as we use the mapped upload instead.
    if (typeof uploadsKeyedByCachedMediaId[item.id] !== 'undefined') {
      return;
    }

    // Determine isGif, must compensate for null
    const isGif = item.__typename === 'Photo' && item.gif ? item.gif : false;
    const length = item.__typename === 'Video' && item.length ? item.length : 0;

    mappedItemsArray.push({
      id: item.id,
      masonryId: item.id,
      type: MediaType[item.type],
      origin: item.origin,
      isGif: isGif,
      width: item.width,
      height: item.height,
      fileName: item.source || '', // this should be removed?
      dateUploaded: item.dateUploaded,
      extension: PhotoExtensions.JPG,
      thumbnailUrl: item.thumbnailUrl,
      length,
      ...(item.__typename === 'Photo' || item.__typename === 'Video'
        ? {
            url: item.url || ''
          }
        : {}),
      ...(item.__typename === 'Video'
        ? {
            transcoded: item.transcoded
          }
        : {}),
      isUploading: false,
      fileSize: item.filesize ?? 0
    });
  });

  return mappedItemsArray;
};

// returned mapped media base properties conditioned on completed media item
const mapMediaBaseProperties = (
  item: MediaItemUpload,
  completedItem: NonNullable<MediaItemUpload['completedMediaItem']> | null
) => ({
  id: completedItem?.id ?? `{PLACEHOLDER}-${item.id}`,
  url: completedItem?.url ?? item.url ?? '',
  thumbnailUrl: completedItem?.thumbnailUrl ?? item.file?.preview ?? '',
  fileSize: completedItem?.filesize ?? item.file?.fileSize ?? 0,
  isGif: completedItem?.__typename === 'Photo' ? completedItem.gif : item.isGif,
  length: completedItem?.__typename === 'Video' ? completedItem.length ?? 0 : item.length ?? 0,
  width: completedItem?.width || item.width,
  height: completedItem?.height || item.height,
  proportion: completedItem?.proportions || item.proportion
});

const mapUploadsToMediaItems = (
  uploads: Record<string, MediaItemUpload>,
  mediaItems: GetMediaLibraryQuery['workspace']['mediaLibrary']['items'] | undefined,
  appliedFilters: MediaLibraryFilterType
): MediaItem[] => {
  const mappedItemsArray: MediaItem[] = [];

  Object.values(uploads).forEach(item => {
    // Check for filters - do we even want to do this?
    if (item.status === MediaUploadStatus.FAILED || item.status === MediaUploadStatus.DELETED) {
      return;
    }

    const completedItem = item.completedMediaItem ? item.completedMediaItem : null;
    const mediaBaseProperties = mapMediaBaseProperties(item, completedItem);

    const mappedItem: MediaItem = {
      ...mediaBaseProperties,
      masonryId: `{PLACEHOLDER}-${item.id}`,
      type: completedItem?.type || item.type,
      origin: mapSourceToOrigin(item.source),
      fileName: '',
      extension: item.type === MediaType.VIDEO ? VideoExtensions.MPG : PhotoExtensions.JPG,
      dateUploaded: '' + item.timeUploadStarted, // Always maintain dateUploaded so we do not cause flipping in view
      isUploading: item.status !== MediaUploadStatus.COMPLETED
    };

    const isMediaInScope = MediaLibraryHelpers.isMediaInFilterScope(mappedItem, item.status, appliedFilters);

    // Remove ones that don't belong in the filter, and duplicates.
    // The duplicate check works because the id is always mapped to the database ID once the upload is complete, and
    // the backend will re-use the same image in the DB.
    if (isMediaInScope && !mappedItemsArray.find(existingItem => existingItem.id === mediaBaseProperties.id)) {
      mappedItemsArray.push(mappedItem);
    }
  });

  return mappedItemsArray.sort((a, b) => parseInt(b.dateUploaded) - parseInt(a.dateUploaded));
};

const mapThirdPartyItemToMediaItems = (items: FindImagesResultsType): MediaItem[] => {
  const mappedItemsArray: MediaItem[] = [];

  if (!items) {
    return [];
  }

  items?.forEach(item => {
    const id = getUploadIdFromFileOrUrl(item.url);

    mappedItemsArray.push({
      id: `${id}`, // This can be unique
      masonryId: `${id}`, // This can be unique
      type: MediaType[item.mediaType],
      origin: MediaOrigin.WEB,
      isGif: item.gif,
      length: null,
      width: 0,
      height: 0,
      fileName: '',
      dateUploaded: Date.now().toString(),
      extension: PhotoExtensions.JPG,
      thumbnailUrl: item.previewUrl,
      url: item.url,
      isUploading: false,
      fileSize: 0,
      createdBy: item.createdBy || undefined,
      createdByUsername: item.createdByUsername || undefined
    });
  });

  return mappedItemsArray;
};

const mapTypeToFilterType = (media: MediaItem): MediaTypeFilter => {
  switch (media.type) {
    case MediaType.VIDEO:
      return MediaTypeFilter.VIDEOS;
    case MediaType.PHOTO:
      if (media.isGif) return MediaTypeFilter.GIFS;
      return MediaTypeFilter.IMAGES;
    default:
      return media.type;
  }
};

const mapSourceToOrigin = (source: string): MediaOrigin => {
  switch (source) {
    case 'url':
      return MediaOrigin.WEB;
    case 'file':
      return MediaOrigin.UPLOAD;
    default:
      return MediaOrigin.UPLOAD;
  }
};

const isFilteringActive = (filters: MediaLibraryFilterType): boolean => {
  return !!Object.values(filters).find(f => f !== '');
};

export const MediaLibraryHelpers = {
  addItemToMediaLibraryCache,
  mediaLibraryFilterReducer,
  findImagesFilterReducer,
  isMediaInFilterScope,
  mapCachedMediaToMediaItems,
  mapUploadsToMediaItems,
  mapThirdPartyItemToMediaItems,
  isFilteringActive
};
